import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {safeBigIntToNumber} from "../../helpers/number_coerce";
import {applyRedactFont, redactText} from "../../helpers/redact";
import {
    asnRttToMs,
    colorForMedianRtt,
    formatMedianRtt,
    formatTrafficVolume,
    medianRttStatusLabel,
    TRAFFIC_OVERVIEW_SHAPER_COLOR,
} from "../../helpers/traffic_overview";

export class ShaperAsnTopDownload extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
    }

    title() {
        return "Shaper Top ASN Download";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Top ASNs (Download)</h5><p>Shows which outside networks are sending the most traffic into this shaper. Width shows selected-period traffic volume and color shows median RTT status.</p>";
    }

    buildContainer() {
        const base = super.buildContainer();
        const note = document.createElement("div");
        note.classList.add("px-3", "pb-2", "small", "text-muted");
        note.textContent = "Outside networks sending traffic into this shaper. Width shows selected-period volume. Color shows median RTT.";
        base.appendChild(note);
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperAsnTopDownloadGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return ["ShaperAsn"];
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_asn") return;

        const rows = [...msg.Data.blob.rows].sort(
            (a, b) => safeBigIntToNumber(b.bytes_down) - safeBigIntToNumber(a.bytes_down)
        );

        const myData = rows.slice(0, 9).map((row) => ({
            name: row.name,
            value: safeBigIntToNumber(row.bytes_down),
            flow_count: safeBigIntToNumber(row.flow_count),
            bytes_up: safeBigIntToNumber(row.bytes_up),
            rttMs: asnRttToMs(row.rtt_down_median),
        }));

        if (!this.rendered) {
            const myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            const myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }

        this.graph.update(myData);
        this.lastGraphState = myData;
        this.rendered = true;
    }
}

class ShaperAsnTopDownloadGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.currentData = [];
        this.option = {
            tooltip: {
                trigger: "item",
                triggerOn: "mousemove",
                formatter: (params) => this.tooltipFormatter(params),
            },
            series: [
                {
                    type: "sankey",
                    data: [],
                    links: [],
                    emphasis: {
                        focus: "adjacency",
                    },
                    lineStyle: {
                        color: "gradient",
                        curveness: 0.5,
                    },
                },
            ],
        };
        this.chart.setOption(this.option);

        window.addEventListener("lqos:redact-changed", () => {
            if (this.currentData.length) {
                this.update(this.currentData);
            }
        });
    }

    tooltipFormatter(params) {
        if (params.dataType === "edge") {
            const edge = params.data || {};
            return `
                <strong>${redactText(edge.source)}</strong><br/>
                Traffic coming into this shaper<br/>
                Volume: ${formatTrafficVolume(edge.value)}<br/>
                RTT: ${formatMedianRtt(edge.rttMs)}<br/>
                Status: ${medianRttStatusLabel(edge.rttMs)}
            `;
        }
        if (params.dataType === "node") {
            if (params.name === "Shaper") {
                return "Shaper";
            }
            const node = this.currentData.find((entry) => entry.name === params.name);
            if (!node) {
                return redactText(params.name);
            }
            return `
                <strong>${redactText(params.name)}</strong><br/>
                Traffic coming into this shaper<br/>
                Volume: ${formatTrafficVolume(node.value)}<br/>
                RTT: ${formatMedianRtt(node.rttMs)}<br/>
                Status: ${medianRttStatusLabel(node.rttMs)}
            `;
        }
        return "";
    }

    update(data) {
        this.chart.hideLoading();
        this.currentData = data;

        const nodes = [{
            name: "Shaper",
            x: 700,
            y: 300,
            itemStyle: {color: TRAFFIC_OVERVIEW_SHAPER_COLOR},
            label: {show: false},
        }];
        const links = [];

        data.forEach((item, index) => {
            const label = applyRedactFont({
                formatter: (params) => redactText(params.name),
            });
            nodes.push({
                name: item.name,
                x: 100,
                y: 50 + (index * 60),
                itemStyle: {color: colorForMedianRtt(item.rttMs)},
                label,
            });
            links.push({
                source: item.name,
                target: "Shaper",
                value: item.value,
                rttMs: item.rttMs,
            });
        });

        this.option.series[0] = {
            ...this.option.series[0],
            data: nodes,
            links,
            left: "5%",
            right: "5%",
            top: "5%",
            bottom: "5%",
            nodeWidth: 20,
            nodeGap: 10,
            layoutIterations: 0,
            labelLayout: {
                hideOverlap: true,
            },
        };

        this.chart.setOption(this.option);
    }
}
